/* Extern */
import React, {
  Component, useEffect, useRef, useState
} from 'react';
import {
  Icon,
  Image,
  Item,
  Menu,
} from 'semantic-ui-react';
import { withRouter } from 'next/router';
// @ts-ignore
import * as compose from 'lodash.flowright';
import { inject, observer } from 'mobx-react';
import Link from 'next/link';

/* Intern */
import Avatar from './Avatar';
import { asset, organizationIsSchool } from '../lib/Util';
import { __ } from '../lib/I18n';

/**
 * @typedef {Omit<import('../lib/Util').FetchedOrganizationFromUser, 'permissions'> & {
 *  accessType: string
 *  entityScopes: string[]
 *  paymentMethods?: import('../lib/Util').PaymentMethodType[]
 * }} Organization
 */

/**
 * @param {{
 *  organizations: Organization[],
 *  onChooseOrganization: (arg: Organization) => void,
 *  addOrganization?: false | (() => void),
 *  onLogout: () => void,
 *  visible: boolean,
 *  onMenuUnfocus: (arg: EventTarget?) => void
 * }} params
 */
export const OrganizationsMenu = ({
  organizations, onChooseOrganization, addOrganization, onLogout, visible = true, onMenuUnfocus = () => { }
}) => {
  const ref = useRef(null);

  /** @param {MouseEvent} event */
  const handleClickOutside = (event) => {
    // @ts-ignore - TODO fix ref.current never type
    if (ref.current && !ref.current.contains(event.target)) onMenuUnfocus(event.target);
  };

  useEffect(() => {
    document.addEventListener('click', handleClickOutside, true);
    return () => document.removeEventListener('click', handleClickOutside, true);
  });

  return (
    <div ref={ref}>
      <Menu hidden={!visible} vertical className="organization-floating-menu">
        <Menu.Item header content="Suas Instituições" />
        {organizations.map(organization => (
          <Menu.Item
            onClick={() => onChooseOrganization(organization)}
            key={organization.id}
          >
            <Avatar size="1.5" alt={organization.fullname} src={organization.logo?.uri} />
            <span style={{ verticalAlign: 'middle' }}>{organization.fullname}</span>
          </Menu.Item>
        ))}
        {addOrganization && (
          <Menu.Item
            onClick={() => addOrganization()}
          >
            <span style={{ verticalAlign: 'middle' }}>
              <i className="icon plus" style={{ marginRight: '.5rem' }} />
              {__('Adicionar instituição')}
            </span>
          </Menu.Item>
        )}
        {onLogout && (
          <Menu.Item onClick={onLogout} key="logout">
            <Icon name="sign out" />
            {__('Sair')}
          </Menu.Item>
        )}
      </Menu>
    </div>
  );
};

/**
 * @param {{
 *  selectedOrganization: Organization | undefined,
 *  organizations: Organization[],
 *  addOrganization?: false | (() => void)
 *  onLogout: () => void,
 *  onChooseOrganization: (arg: Organization) => void
 * }} params
 */
export const SelectOrganizationButton = ({
  selectedOrganization, organizations, addOrganization, onLogout, onChooseOrganization
}) => {
  const [showMenu, setShowMenu] = useState(false);
  const buttonRef = useRef(null);

  return (
    <div>
      <button
        ref={buttonRef}
        type="button"
        className="transparent"
        onClick={() => setShowMenu(!showMenu)}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          maxWidth: '100%'
        }}
      >
        <div style={{
          display: 'flex', alignItems: 'center', overflow: 'hidden', whiteSpace: 'nowrap'
        }}
        >
          {selectedOrganization && <Avatar alt={selectedOrganization.fullname} src={selectedOrganization?.logo?.uri} />}
          <span style={{ marginLeft: '10px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {selectedOrganization?.fullname}
          </span>
        </div>
        <div className="coloured gray" style={{ display: 'flex', margin: '0.3rem auto', justifyContent: 'space-between' }}>
          <span style={{ marginRight: 5, marginLeft: 15 }}>{__('Trocar')}</span>
          <Icon name="angle down" />
        </div>
      </button>
      <OrganizationsMenu
        visible={showMenu}
        organizations={organizations}
        onChooseOrganization={organization => {
          onChooseOrganization(organization);
          setShowMenu(false);
        }}
        addOrganization={addOrganization}
        onLogout={onLogout}
        onMenuUnfocus={(target) => {
          // @ts-ignore - TODO fix buttonRef.current.contains never type
          if (buttonRef.current && !buttonRef.current.contains(target)) {
            setShowMenu(false);
          }
        }}
      />
    </div>
  );
};

/**
 * @param {{
 *  selectedOrganization: Organization,
 *  organizations: Organization[],
 *  expanded: boolean,
 *  onChooseOrganization: (agr: Organization) => void,
 *  addOrganization: () => void,
 *  onLogout: () => void
 * }} params
 */
const OrganizationsDropdown = ({
  selectedOrganization, organizations, expanded, onChooseOrganization, addOrganization, onLogout
}) => {
  const buttonRef = useRef(null);
  const [showOrganizationMenu, setShowOrganizationMenu] = useState(false);

  if (!organizations || !organizations.length) return null;

  return (
    <div className="side-menu-organization-bottom">
      <div ref={buttonRef}>
        <Item
          className="side-menu-item"
          onClick={() => setShowOrganizationMenu(!showOrganizationMenu)}
        >
          <div className="item-icon">
            <Avatar alt={selectedOrganization.fullname} src={selectedOrganization.logo?.uri} />
          </div>
          <span hidden={!expanded} style={{ verticalAlign: 'middle', lineHeight: '1.45rem' }}>
            {selectedOrganization.fullname}
          </span>
          <p
            hidden={expanded || showOrganizationMenu}
            className="description"
          >
            {selectedOrganization.fullname}
          </p>
        </Item>
      </div>
      <OrganizationsMenu
        organizations={organizations}
        visible={showOrganizationMenu}
        onChooseOrganization={(organization) => {
          onChooseOrganization(organization);
          setShowOrganizationMenu(false);
        }}
        addOrganization={addOrganization}
        onLogout={onLogout}
        onMenuUnfocus={(target) => {
          // @ts-ignore - TODO fix buttonRef.current.contains never type
          if (buttonRef.current && !buttonRef.current.contains(target)) {
            setShowOrganizationMenu(false);
          }
        }}
      />
    </div>
  );
};

class SideMenu extends Component {
  /** @param {{store: { isMenuExpanded: boolean }}} props */
  constructor(props) {
    super(props);
    this.state = {
      expanded: (window?.innerWidth < 700) ? false : props.store.isMenuExpanded
    };
  }

  menuItems = () => {
    const { store } = this.props;
    const { user } = store;

    const selectedOrganization = user.organizations.find(/** @param {Organization} o */o => o.id === user.selectedOrganizationId);
    const isSchool = organizationIsSchool(selectedOrganization?.type);

    return user?.accessToken ? [ // {
      //   route: '/dashboard',
      //   hover: __('Painel'),
      //   icon: 'chart line up'
      // },
      {
        route: '/revenue',
        hover: __('Receitas'),
        icon: 'sack dollar'
      },
      {
        route: '/entity',
        hover: isSchool ? __('Alunos') : __('Clientes'),
        icon: 'user friends'
      },
      {
        route: '/plan',
        hover: __('Planos'),
        icon: 'sitemap'
      },
      {
        route: '/product',
        hover: __('Produtos'),
        icon: 'shapes'
      },
      // {
      //   route: '/reports',
      //   hover: __('Relatórios'),
      //   icon: 'box ballot'
      // },
      {
        route: '/settings',
        hover: __('Configurações'),
        icon: 'cog'
      },
      ...(process.env.NODE_ENV === 'development' ? [{
        route: '/exemplo',
        hover: 'Exemplos',
        icon: 'cactus'
      }] : [])] : [];
  };

  /**
   * @param {{
   *  route: string,
   *  hover: string,
   *  icon: string
   * }} menuItem
   */
  _renderItem = (menuItem) => {
    const { expanded } = this.state;
    const { router } = this.props;
    const pathname = router.route.split('/')[1];
    const isActive = menuItem.route.slice(1) && menuItem.route.slice(1) === pathname;

    return (
      // eslint-disable-next-line jsx-a11y/click-events-have-key-events
      <Link
        href={menuItem.route}
        key={`side-menu-item-${menuItem.route}`}
      >
        <a style={{ display: 'flex', color: 'inherit' }}>
          <div className={`side-menu-item ${isActive ? 'active' : ''}`}>
            <div className="wrapper">
              <Icon className={`${menuItem.icon} ${isActive ? 'duotone' : ''}`} />

              <span
                hidden={!menuItem.hover || !expanded}
                className={isActive ? 'active' : ''}
              >
                {menuItem.hover}
              </span>
            </div>

            <p hidden={!menuItem.hover || expanded} className="description">{menuItem.hover}</p>
          </div>
        </a>
      </Link>
    );
  };

  render() {
    const { expanded } = this.state;
    const { store, router } = this.props;
    const { user } = store;

    let selectedOrganization = null;
    if (user && user.accessToken && user.organizations) {
      const { selectedOrganizationId, organizations } = user;
      selectedOrganization = organizations.find(
        /** @param {Organization} organization */
        organization => organization.id === selectedOrganizationId
      );
    }

    const existingUser = user && Object.keys(user).length > 0;
    const canAddOrganization = user && user.organizationsToContract?.length > 0;

    return (
      <div className={`side-menu ${expanded && existingUser ? 'expanded' : ''}`}>
        <div className={`inner-side-menu ${expanded && existingUser ? 'expanded' : ''}`}>
          <Link href="/">
            <div className="side-menu-item logo">
              <Image src={asset('classapp-compact-logo.svg')} />
              <Image
                className="classapp-name"
                hidden={!expanded || !existingUser}
                src={asset('classapp-name.svg')}
              />
            </div>
          </Link>

          {existingUser && this.menuItems().map(item => this._renderItem(item))}

          {existingUser && (
            <Item
              className="side-menu-item trigger"
              onClick={() => {
                this.setState({ expanded: !expanded });
                store.setIsMenuExpanded(!expanded);
              }}
            >
              <div className="item-icon">
                <Icon className={expanded ? 'angle left' : 'angle right'} />
              </div>
              <p className="description">{expanded ? __('Minimizar menu') : __('Expandir menu')}</p>
            </Item>
          )}

          {existingUser && (
            <OrganizationsDropdown
              selectedOrganization={selectedOrganization}
              organizations={user.organizations}
              expanded={expanded && existingUser}
              onChooseOrganization={organization => {
                this.props.store.changeSelectedOrganization(organization);
              }}
              addOrganization={canAddOrganization && (() => router.push('/serviceContract'))}
              onLogout={() => this.props.store.logout()}
            />
          )}
        </div>
      </div>
    );
  }
}

export default compose(
  withRouter,
  inject('store'),
  observer
)(SideMenu);
